import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Palette } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme, THEME_LABELS, Theme } from "@/lib/themeContext";

const THEME_COLORS: Record<Theme, string> = {
  light: "hsl(245,65%,18%)",
  dark: "hsl(45,85%,50%)",
  slate: "hsl(215,65%,35%)",
  "slate-dark": "hsl(195,80%,55%)",
  warm: "hsl(20,70%,35%)",
  "warm-dark": "hsl(40,90%,60%)",
  forest: "hsl(150,60%,22%)",
  "forest-dark": "hsl(80,60%,52%)",
  midnight: "hsl(270,65%,40%)",
  "midnight-dark": "hsl(300,70%,65%)",
};

const LIGHT_ROW: Theme[] = ["light", "slate", "warm", "forest", "midnight"];
const DARK_ROW: Theme[] = ["dark", "slate-dark", "warm-dark", "forest-dark", "midnight-dark"];

interface ThemeSwitcherProps {
  isCollapsed?: boolean;
}

export function ThemeSwitcher({ isCollapsed = false }: ThemeSwitcherProps) {
  const { theme, setTheme } = useTheme();

  function SwatchRow({ themes, label }: { themes: Theme[]; label: string }) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground w-8 shrink-0">{label}</span>
        {themes.map((t) => (
          <Tooltip key={t}>
            <TooltipTrigger asChild>
              <button
                onClick={() => setTheme(t)}
                className={cn(
                  "w-6 h-6 rounded-full transition-all hover:scale-110 shrink-0",
                  t === theme && "ring-2 ring-offset-2 ring-foreground"
                )}
                style={{ backgroundColor: THEME_COLORS[t] }}
                aria-label={THEME_LABELS[t]}
              />
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{THEME_LABELS[t]}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        {isCollapsed ? (
          <button
            className="flex items-center justify-center p-2 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
            aria-label="Switch theme"
          >
            <Palette className="w-4 h-4 text-sidebar-primary" />
          </button>
        ) : (
          <button className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors">
            <Palette className="w-4 h-4 shrink-0 text-sidebar-primary" />
            <span>{THEME_LABELS[theme]}</span>
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3" side="top" align="start">
        <TooltipProvider delayDuration={200}>
          <div className="flex flex-col gap-2">
            <SwatchRow themes={LIGHT_ROW} label="Light" />
            <SwatchRow themes={DARK_ROW} label="Dark" />
          </div>
        </TooltipProvider>
      </PopoverContent>
    </Popover>
  );
}
